import * as yup from 'yup';
import { DocumentNode } from 'graphql';
import { Route, Switch } from 'react-router-dom';
import { Shield, ShieldOff, Trash } from 'react-feather';
import { Trans, useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-form-state-manager';
import groupBy from 'lodash/groupBy';

import {
  GetAuthenticatorSecretQuery as GetAuthenticatorSecret,
  GetPortalQuery as GetPortal,
} from '__generated__/graphql';

import { Breadcrumbs, Container, NavItem, Section } from '../Common/Layout';
import { GetCurrentUserQuery, useValidateUserEmail } from '../../common/Auth/useAuth';
import { Routes, useNavigation } from '../Common/useNavigation';
import {
  emailRegex, getServerErrors, isGraphQLAuthenticationError, isGraphQLAuthenticatorRequiredError,
  setWindowLocation,
} from '../../common/helpers';
import { useNotifier } from '../../common/Notifications/NotificationProvider';
import CleanupCurrentUserMutation from '../Common/CleanupCurrentUserMutation';
import EditCurrentUserMutation from '../Common/EditCurrentUserMutation';
import EmailInput from '../../common/ParticipantAttributes/Attributes/EmailInput';
import GetAuthenticatorSecretQuery from './GetAuthenticatorSecretQuery';
import GetPortalQuery from './GetPortalQuery';
import UI from '../../common/UI';
import config from '../../config';
import useAuthenticatorVerificationModal from '../../common/useAuthenticatorVerificationModal';
import useLazyQuery from '../../api/useLazyQuery';
import useMutation from '../../api/useMutation';
import useQuery from '../../api/useQuery';
import useScroll from '../../common/useScroll';
import useValidation from '../../common/useValidation';

type User = GetPortal['session']['user'];

interface AccountPageProps {
  refetchQueries?: DocumentNode[];
}

const AccountPage = ({ refetchQueries = [GetPortalQuery] }: AccountPageProps) => {
  const { t } = useTranslation();

  const { data } = useQuery(GetPortalQuery);

  const user = data?.session.user;

  if (!user) {
    return <UI.PageLoader />;
  }

  return (
    <Container>
      <Breadcrumbs>
        <UI.Strong>{t('account')}</UI.Strong>
      </Breadcrumbs>

      <Switch>
        <Route path={Routes.AccountDelete}>
          <DeleteAccountPage user={user} refetchQueries={refetchQueries} />
        </Route>

        <Route>
          <EditAccountPage user={user} refetchQueries={refetchQueries} />
        </Route>
      </Switch>
    </Container>
  );
};

const getEditUserSchema = (emailConfirmed: boolean) => (
  yup.object().shape({
    first_name: yup.string().required(),
    last_name: yup.string().required(),
    email: yup.string().required().matches(emailRegex, { name: 'email' }),
    email_repeat: yup.string().test('invalid', '', () => emailConfirmed),
  })
);

const getValues = (user: Pick<User, 'first_name' | 'last_name' | 'email' | 'authenticator_enabled'>) => ({
  first_name: user.first_name,
  last_name: user.last_name,
  email: user.email,
  email_repeat: '',
  authenticator_enabled: user.authenticator_enabled,
  authenticator_code: null as string | null,
});

interface EditAccountPageProps {
  user: User;
  refetchQueries?: DocumentNode[];
}

const EditAccountPage = ({ user, refetchQueries = [GetCurrentUserQuery] }: EditAccountPageProps) => {
  const { t } = useTranslation();
  const { scrollToTop } = useScroll();
  const { r } = useNavigation();
  const notifier = useNotifier();

  const [redirecting, setRedirecting] = useState(false);

  const form = useForm({
    values: getValues(user),
  });

  // Get the form values without email repeat.
  const { email_repeat: emailRepeat, ...formValues } = form.values;

  const [getAuthenticatorSecret, { data }] = useLazyQuery<GetAuthenticatorSecret>(GetAuthenticatorSecretQuery);

  const authenticatorSecret = data?.authenticatorSecret.secret;
  const authenticatorQr = data?.authenticatorSecret.qr;

  const [showAuthenticatorVerificationModal] = useAuthenticatorVerificationModal({
    onSuccess: () => editUser(),
  });

  const [editUser, { error, loading, client }] = useMutation(
    EditCurrentUserMutation,
    {
      variables: {
        input: {
          ...formValues,
          authenticator_secret: authenticatorSecret,
        },
      },
      onCompleted: ({ editCurrentUser }) => {
        notifier.success(t('information_saved'));
        form.reset(getValues(editCurrentUser));
        scrollToTop();
      },
      onError: (error) => {
        if (isGraphQLAuthenticatorRequiredError(error)) {
          showAuthenticatorVerificationModal();
        } else if (isGraphQLAuthenticationError(error)) { // If the e-mail address was changed.
          setRedirecting(true);
          // Normal refetchQueries option does not run on error, so we must explicitly call it here
          client.refetchQueries({ include: refetchQueries });
        }

        scrollToTop();
      },
      refetchQueries,
    },
  );

  const emailChanged = form.changed('email')
    && form.values.email.toLowerCase() !== user.email.toLowerCase();
  const emailConfirmed = !emailChanged
    || form.values.email.toLowerCase() === form.values.email_repeat.toLowerCase();

  const editUserSchema = useMemo(
    () => getEditUserSchema(emailConfirmed),
    [emailConfirmed],
  );

  const { valid, errors } = useValidation({
    schema: editUserSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  const { userExists, loading: validationLoading } = useValidateUserEmail(form.values.email, {
    skip: !emailChanged,
  });

  const showRepeatEmailField = emailChanged && !userExists && !errors.email;
  const showUserExistsWarning = emailChanged && !validationLoading && userExists && !errors.email;
  const showAuthenticatorSetup = !user.authenticator_enabled && form.values.authenticator_enabled;

  useEffect(() => {
    if (showAuthenticatorSetup && !authenticatorSecret) {
      getAuthenticatorSecret();
    }
  }, [showAuthenticatorSetup, authenticatorSecret, getAuthenticatorSecret]);

  const mailPreferenceParticipants = user.participants.filter(
    (participant) => participant.available_mail_permissions.length > 0,
  );

  return (
    <UI.GridContainer>
      <Section title={t('edit_account')} spacing="md">
        <UI.Form onSubmit={() => editUser()}>
          <UI.FormGrid>
            {!redirecting && !isGraphQLAuthenticatorRequiredError(error) && <UI.ServerErrors error={error} />}

            {redirecting && (
              <UI.Info>
                {t('redirecting')}
              </UI.Info>
            )}

            <UI.FormGrid sc={{ columns: '1fr 1fr' }}>
              <UI.InputGroup sc={{ valid: !errors.first_name, touched: form.touched.first_name }}>
                <UI.InputLabel htmlFor="UserFirstName">
                  {t('first_name')}
                  {' '}
                  <UI.RequiredMark />
                </UI.InputLabel>
                <UI.DebouncedInput id="UserFirstName" {...form.text('first_name')} required />
                <UI.ErrorMessages attribute={t('first_name')} errors={errors.first_name} />
              </UI.InputGroup>
              <UI.InputGroup sc={{ valid: !errors.last_name, touched: form.touched.last_name }}>
                <UI.InputLabel htmlFor="UserLastName">
                  {t('last_name')}
                  {' '}
                  <UI.RequiredMark />
                </UI.InputLabel>
                <UI.DebouncedInput id="UserLastName" {...form.text('last_name')} required />
                <UI.ErrorMessages attribute={t('last_name')} errors={errors.last_name} />
              </UI.InputGroup>
            </UI.FormGrid>

            <EmailInput
              value={form.values}
              onChange={(value) => form.set((user) => ({ ...user, ...value }))}
              onBlur={(key) => form.setTouched(key, true)}
              touched={form.touched}
              errors={errors}
              required
            />

            {showRepeatEmailField && (
              <UI.InputGroup sc={{ valid: !errors.email_repeat, touched: form.touched.email_repeat }}>
                <UI.InputLabel htmlFor="UserEmailRepeat">
                  {t('email_address_repeat')}
                  {' '}
                  <UI.RequiredMark />
                </UI.InputLabel>
                <UI.InputDescription>
                  {t('change_email_description')}
                </UI.InputDescription>
                <UI.DebouncedInput
                  {...form.text('email_repeat')}
                  inputMode="email"
                  id="UserEmailRepeat"
                />
                <UI.ErrorMessages
                  attribute={t('email_repeat')}
                  errors={errors.email_repeat}
                />
              </UI.InputGroup>
            )}

            {showUserExistsWarning && (
              <UI.Warning>
                {t('edit_user_exists_warning')}
              </UI.Warning>
            )}

            {user.authenticator_available && (
              <>
                <UI.HR sc={{ dashed: true }} />

                <UI.InputGroup
                  sc={{ valid: !errors.authenticator_enabled, touched: form.touched.authenticator_enabled }}
                >
                  <UI.InputLabel htmlFor="TwoStepVerification">
                    {t('two_step_verification')}
                  </UI.InputLabel>
                  <UI.InputDescription>
                    {t('two_step_verification_description')}
                  </UI.InputDescription>
                  <UI.RadioPill sc={{ vertical: true }}>
                    <UI.Radio
                      {...form.radio('authenticator_enabled', true)}
                      sc={{ box: true, flex: { justifyContent: 'space-between', alignItems: 'center' } }}
                      id="TwoStepVerification"
                    >
                      <UI.Strong sc={{ fontWeight: 500 }}>
                        {t('enable')}
                      </UI.Strong>
                      <UI.Icon sc={{ color: 'success', mb: 0, size: 1.125 }}>
                        <Shield />
                      </UI.Icon>
                    </UI.Radio>
                    <UI.Radio
                      {...form.radio('authenticator_enabled', false)}
                      sc={{ box: true, flex: { justifyContent: 'space-between', alignItems: 'center' } }}
                    >
                      <UI.Strong sc={{ fontWeight: 500 }}>
                        {t('disable')}
                      </UI.Strong>
                      <UI.Icon sc={{ color: 'error', mb: 0, size: 1.125 }}>
                        <ShieldOff />
                      </UI.Icon>
                    </UI.Radio>
                  </UI.RadioPill>
                  <UI.ErrorMessages attribute={t('two_step_verification')} errors={errors.authenticator_enabled} />
                </UI.InputGroup>
              </>
            )}

            {showAuthenticatorSetup && (
              <UI.Card sc={{ outline: 'gray.200', borderRadius: 'md', spacing: 'sm' }}>
                {!authenticatorQr && <UI.Loader />}
                {authenticatorQr && (
                  <UI.FormGrid>
                    <UI.Div>
                      <UI.FlexContainer sc={{ alignItems: 'center', spaceX: 2, mb: 1 }}>
                        <UI.Badge sc={{ size: 18 }}>
                          1
                        </UI.Badge>
                        <UI.H4>
                          {t('open_your_authenticator_app')}
                        </UI.H4>
                      </UI.FlexContainer>

                      <UI.InputDescription>
                        {t('authenticator_setup_description')}
                      </UI.InputDescription>
                    </UI.Div>

                    <img src={authenticatorQr} height={200} alt={t('authenticator_code')} />

                    <UI.HR />

                    <UI.Div>
                      <UI.FlexContainer sc={{ alignItems: 'center', spaceX: 2, mb: 1 }}>
                        <UI.Badge sc={{ size: 18 }}>
                          2
                        </UI.Badge>
                        <UI.H4>
                          {t('enter_authenticator_code')}
                        </UI.H4>
                      </UI.FlexContainer>

                      <UI.InputDescription>
                        {t('enter_authenticator_code_description')}
                      </UI.InputDescription>
                    </UI.Div>

                    <UI.InputGroup sc={{ invalid: error && !!errors.authenticator_code }}>
                      <UI.InputLabel htmlFor="Pin_0">
                        {t('authenticator_code')}
                      </UI.InputLabel>
                      <UI.PinCodeInput
                        value={form.values.authenticator_code}
                        onChange={(value) => form.set('authenticator_code', value)}
                        length={config.authenticatorCodeLength}
                        name="authenticator_code"
                        id="Pin"
                        autoFocus
                        placeholder="123456"
                        spacingAfter={[2]}
                        inputMode="numeric"
                        sc={{ size: 'lg' }}
                      />
                      <UI.ErrorMessages attribute={t('authenticator_code')} errors={errors.authenticator_code} />
                    </UI.InputGroup>
                  </UI.FormGrid>
                )}
              </UI.Card>
            )}

            <UI.HR sc={{ dashed: true }} />

            <UI.Button
              type="submit"
              sc={{ brand: 'secondary', block: true }}
              disabled={loading || !valid || !form.changed()}
            >
              {t('save')}
            </UI.Button>
          </UI.FormGrid>
        </UI.Form>
      </Section>

      {mailPreferenceParticipants.length > 0 && (
        <Section title={t('email_preferences')} spacing="md">
          {mailPreferenceParticipants.map((participant) => (
            <NavItem
              to={r('MailPreferences', {
                projectId: participant.project.id,
                participantId: participant.id,
              })}
              alignVertical="center"
              title={participant.project.organisation_name}
              key={participant.id}
            >
              <UI.H4>
                {participant.project.organisation_name}
              </UI.H4>
            </NavItem>
          ))}
        </Section>
      )}

      <UI.Div>
        <UI.Link to={Routes.AccountDelete} sc={{ secondary: true, small: true }}>
          {t('delete_account')}
        </UI.Link>
      </UI.Div>
    </UI.GridContainer>
  );
};

interface DeleteAccountPageProps {
  user: User;
  refetchQueries?: DocumentNode[];
}

const DeleteAccountPage = ({ user, refetchQueries }: DeleteAccountPageProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();

  const summariesByProject = groupBy(user.registration_summaries, (summary) => summary.event.project.id);

  const deletableProjects: User['participants'][0]['project'][] = [];
  const undeletableProjects: User['participants'][0]['project'][] = [];

  user.participants.forEach((participant) => {
    const { project } = participant;
    const summaries = summariesByProject[project.id] || [];

    if (summaries.some((summary) => !summary.deletable_by_participant)) {
      undeletableProjects.push(project);
    } else {
      deletableProjects.push(project);
    }
  });

  const deletableMemberships: typeof user.project_memberships = [];
  const undeletableMemberships: typeof user.project_memberships = [];

  user.project_memberships.forEach((membership) => {
    if (membership.deletable) {
      deletableMemberships.push(membership);
    } else {
      undeletableMemberships.push(membership);
    }
  });

  const hasDeletableData = deletableProjects.length + undeletableProjects.length
    + deletableMemberships.length + undeletableMemberships.length > 0;

  const accountIsDeletable = undeletableProjects.length === 0 && undeletableMemberships.length === 0;
  const allowDelete = deletableProjects.length > 0 || deletableMemberships.length > 0 || accountIsDeletable;

  const [confirmDelete, setConfirmDelete] = useState(false);

  const [deleteUser, { error, loading }] = useMutation(
    CleanupCurrentUserMutation,
    {
      onCompleted: ({ cleanupCurrentUser }) => {
        // If the user was fully deleted, redirect to the homepage.
        if (cleanupCurrentUser) {
          setWindowLocation('/');
        } else {
          notifier.success(t('information_saved'));
        }
      },
      refetchQueries,
    },
  );

  return (
    <UI.GridContainer>
      <Section spacing="md" title={t('delete_account')}>
        <UI.Form onSubmit={() => deleteUser()}>
          <UI.FormGrid>
            <UI.ServerErrors error={error} />

            {hasDeletableData && (
              <UI.FormGrid sc={{ gutter: 0.5 }}>
                {deletableProjects.length > 0 && (
                  <UI.Info icon={<Trash />}>
                    <UI.H5 sc={{ strong: true }}>
                      {t('delete_account_form.participant_data_will_be_deleted_label')}
                    </UI.H5>
                    <UI.Paragraph sc={{ mb: 1 }}>
                      {t('delete_account_form.participant_data_will_be_deleted_description')}
                    </UI.Paragraph>
                    <UI.UL>
                      {deletableProjects.map((project) => (
                        <UI.LI key={project.id}>
                          {project.organisation_name}
                          {summariesByProject[project.id]?.length > 0 && (
                            <>
                              {' ('}
                              <UI.ListLimiter
                                items={summariesByProject[project.id].map((summary) => summary.event.title)}
                                limit={1}
                              />
                              )
                            </>
                          )}
                        </UI.LI>
                      ))}
                    </UI.UL>
                  </UI.Info>
                )}

                {deletableMemberships.length > 0 && (
                  <UI.Info icon={<Trash />}>
                    <UI.H5 sc={{ strong: true }}>
                      {t('delete_account_form.will_lose_organiser_access_label')}
                    </UI.H5>
                    <UI.Paragraph sc={{ mb: 1 }}>
                      {t('delete_account_form.will_lose_organiser_access_description')}
                    </UI.Paragraph>
                    <UI.UL>
                      {deletableMemberships.map((membership) => (
                        <UI.LI key={membership.id}>
                          {membership.project.organisation_name}
                        </UI.LI>
                      ))}
                    </UI.UL>
                  </UI.Info>
                )}

                {undeletableProjects.length > 0 && (
                  <UI.Warning>
                    <UI.H5 sc={{ strong: true }}>
                      {t('delete_account_form.cannot_delete_active_registrations_label')}
                    </UI.H5>
                    <UI.Paragraph sc={{ mb: 1 }}>
                      {t('delete_account_form.cannot_delete_active_registrations_description')}
                    </UI.Paragraph>
                    <UI.UL>
                      {undeletableProjects.map((project) => (
                        <UI.LI key={project.id}>
                          {project.organisation_name}
                          {summariesByProject[project.id]?.length > 0 && (
                            <>
                              {' ('}
                              <UI.ListLimiter
                                items={summariesByProject[project.id].map((summary) => summary.event.title)}
                                limit={1}
                              />
                              )
                            </>
                          )}
                        </UI.LI>
                      ))}
                    </UI.UL>
                  </UI.Warning>
                )}

                {undeletableMemberships.length > 0 && (
                  <UI.Warning>
                    <UI.H5 sc={{ strong: true }}>
                      {t('delete_account_form.cannot_revoke_admin_access_label')}
                    </UI.H5>
                    <UI.Paragraph sc={{ mb: 1 }}>
                      {t('delete_account_form.cannot_revoke_admin_access_description')}
                    </UI.Paragraph>
                    <UI.UL>
                      {undeletableMemberships.map((membership) => (
                        <UI.LI key={membership.id}>
                          {membership.project.organisation_name}
                        </UI.LI>
                      ))}
                    </UI.UL>
                  </UI.Warning>
                )}
              </UI.FormGrid>
            )}

            <UI.InputGroup>
              <UI.InputLabel>
                {t('account')}
              </UI.InputLabel>
              <UI.Div>
                {user.email}
              </UI.Div>
            </UI.InputGroup>

            {allowDelete && (
              <>
                <UI.InputGroup>
                  <UI.InputLabel htmlFor="ConfirmDelete">
                    {t('confirmation')}
                  </UI.InputLabel>
                  <UI.Checkbox
                    checked={confirmDelete}
                    onChange={() => setConfirmDelete((confirmDelete) => !confirmDelete)}
                    id="ConfirmDelete"
                  >
                    {t('delete_account_form.yes_delete_data')}
                  </UI.Checkbox>
                </UI.InputGroup>

                <UI.HR sc={{ dashed: true }} />

                <UI.Paragraph>
                  <Trans
                    i18nKey={accountIsDeletable
                      ? 'delete_account_form.delete_account_all_data'
                      : 'delete_account_form.delete_account_some_data'}
                  >
                    <UI.Strong />
                  </Trans>

                </UI.Paragraph>

                <UI.FormGrid sc={{ columns: [null, '1fr 1fr'] }}>
                  <UI.LinkButton to={Routes.Account}>
                    {t('cancel')}
                  </UI.LinkButton>
                  <UI.Button
                    type="submit"
                    sc={{ block: true, brand: 'error' }}
                    disabled={loading || !confirmDelete}
                  >
                    {t('delete_data')}
                  </UI.Button>
                </UI.FormGrid>
              </>
            )}
          </UI.FormGrid>
        </UI.Form>
      </Section>
      <UI.Div>
        <UI.Link to={Routes.Account} sc={{ secondary: true, small: true }}>
          {t('edit_account')}
        </UI.Link>
      </UI.Div>
    </UI.GridContainer>
  );
};

export default AccountPage;
